/**
 * ADLSummaryPage
 * CCH Care — Phase 5f / Late Doc Phase 2
 * Last Modified: 2026-05-13
 *
 * Read-only, lightweight view of a completed ADL record.
 * Shows section-by-section signed status, key data highlights,
 * signer identity, and amendment count. No editing.
 *
 * Changes (2026-05-13) — Late Documentation Phase 2:
 * - Mounts ReopenRecordButton between the read-only notice and the
 *   progress bar. The component self-renders based on role and
 *   record state — invisible to CNAs, shows the action button or
 *   the reopened-state badge to Supervisors/Admins.
 * - Refreshes the page via load() on successful reopen so the
 *   button instance transitions to the badge instance.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';
import ReopenRecordButton from '../../components/late-doc/ReopenRecordButton';

import { formatTime, formatDateTime } from '../../utils/formatters';
const BASE = '/shifts/history.php';

const SECTIONS = [
  { key: 'hygiene',       label: 'Hygiene',       icon: '🛁' },
  { key: 'meals',         label: 'Meals',          icon: '🍽️' },
  { key: 'repositioning', label: 'Repositioning',  icon: '🔄' },
  { key: 'fluids',        label: 'Fluid Intake',   icon: '💧' },
  { key: 'bowel',         label: 'Bowel',          icon: '📋' },
  { key: 'urination',     label: 'Urination',      icon: '📋' },
  { key: 'skin',          label: 'Skin Checks',    icon: '🩹' },
  { key: 'safety',        label: 'Safety',         icon: '🔒' },
];

function decodeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&#039;/g, "'").replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
}

function sectionSummary(key, data) {
  if (!data) return null;
  switch (key) {
    case 'meals': {
      const meals = data;
      if (!meals.length) return 'No meals recorded';
      return meals.map(m => {
        const label = m.meal_type.charAt(0).toUpperCase() + m.meal_type.slice(1);
        if (m.refused) return `${label}: Refused`;
        if (m.intake_percent != null) return `${label}: ${m.intake_percent}%`;
        return label;
      }).join(' · ');
    }
    case 'vitals':
      return data.reading_count > 0
        ? `${data.reading_count} reading${data.reading_count > 1 ? 's' : ''}${data.abnormal_count > 0 ? ` · ⚠ ${data.abnormal_count} abnormal` : ''}`
        : 'No readings';
    case 'repositioning':
      return data.entry_count > 0
        ? `${data.entry_count} repositioning${data.entry_count > 1 ? 's' : ''} documented`
        : 'No entries';
    case 'fluids':
      return data.entry_count > 0
        ? `${data.total_ml} ml total (${data.entry_count} entr${data.entry_count > 1 ? 'ies' : 'y'})`
        : 'No fluid intake recorded';
    case 'bowel':
      return data.entry_count > 0
        ? `${data.entry_count} BM${data.entry_count > 1 ? 's' : ''} documented`
        : 'No bowel movements';
    case 'urination':
      return data.entry_count > 0
        ? `${data.entry_count} void${data.entry_count > 1 ? 's' : ''} documented`
        : 'No urination documented';
    case 'skin':
      return data.areas_checked > 0
        ? `${data.areas_checked} area${data.areas_checked > 1 ? 's' : ''} checked${data.concerns > 0 ? ` · ${data.concerns} concern${data.concerns > 1 ? 's' : ''}` : ' · All intact'}`
        : 'No skin checks';
    case 'safety':
      return data.items_checked > 0
        ? `${data.items_checked} safety item${data.items_checked > 1 ? 's' : ''} verified`
        : 'No items checked';
    default:
      return null;
  }
}

export default function ADLSummaryPage() {
  const { shiftId, adlRecordId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord]         = useState(null);
  const [signatures, setSignatures] = useState({});
  const [sectionData, setSectionData] = useState({});
  const [amendments, setAmendments] = useState({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get(`${BASE}?action=adl_summary&adl_record_id=${adlRecordId}`);
      if (res.data.success) {
        setRecord(res.data.record);
        setSignatures(res.data.signatures || {});
        setSectionData(res.data.section_data || {});
        setAmendments(res.data.amendments || {});
      } else {
        setError(res.data.error || 'Failed to load');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load ADL record');
    } finally {
      setLoading(false);
    }
  }, [adlRecordId]);

  useEffect(() => { load(); }, [load]);

  const signedCount = SECTIONS.filter(s => signatures[s.key]).length;
  const supervisor  = signatures['supervisor'];

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', background: COLORS.primary, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          onClick={() => navigate(`/dashboard/shift-history/${shiftId}`)}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '2px 6px' }}
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {record ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {decodeHtml(record.first_name)} {decodeHtml(record.last_name)}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                ADL Record · {record.shift_type} · {new Date(record.shift_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 700 }}>ADL Summary</div>
          )}
        </div>
        {!loading && record && (
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 12px', textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{signedCount}/8</div>
            <div style={{ fontSize: 9, opacity: 0.8, textTransform: 'uppercase' }}>Signed</div>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: COLORS.textMuted }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          Loading record…
        </div>
      )}

      {error && !loading && (
        <div style={{ margin: 16, padding: '12px 16px', borderRadius: 10, background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, color: COLORS.danger, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {!loading && !error && record && (
        <div style={{ padding: 16 }}>

          {/* Read-only notice */}
          <div style={{
            background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: COLORS.primary,
          }}>
            <span style={{ fontSize: 16 }}>🔍</span>
            <span>This is a read-only historical record. No edits can be made here.</span>
          </div>

          {/* Supervisor reopen control — invisible to CNAs, surfaces a
              button to Care Supervisors/Admins when reopen is eligible,
              or an info badge when the record is already in an active
              reopen window. */}
          <ReopenRecordButton record={record} onReopened={load} />

          {/* Progress bar */}
          <div style={{ height: 6, background: COLORS.border, borderRadius: 3, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: signedCount >= 8 ? COLORS.accent : signedCount > 0 ? COLORS.warning : COLORS.border,
              width: `${Math.round((signedCount / 8) * 100)}%`,
            }} />
          </div>

          {/* Supervisor co-sign banner */}
          {supervisor && (
            <div style={{
              background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`,
              borderRadius: 10, padding: '10px 14px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>🔍</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>Supervisor Co-Signed</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  {decodeHtml(supervisor.signer)} ({supervisor.role}) · {formatDateTime(supervisor.signed_at)}
                </div>
              </div>
            </div>
          )}

          {/* Section cards */}
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
            ADL Sections
          </div>

          {SECTIONS.map(section => {
            const sig     = signatures[section.key];
            const summary = sectionSummary(section.key, sectionData[section.key]);
            const amendCount = amendments[section.key] || 0;

            return (
              <div key={section.key} style={{
                background: COLORS.surface, borderRadius: 10,
                border: `1px solid ${sig ? COLORS.accentAlpha : COLORS.border}`,
                marginBottom: 8, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '10px 14px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: sig ? 'rgba(46,204,113,0.04)' : 'transparent',
                }}>
                  {/* Status dot */}
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    background: sig ? COLORS.accent : COLORS.border,
                  }} />

                  <span style={{ fontSize: 16, flexShrink: 0 }}>{section.icon}</span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{section.label}</div>
                    {summary && (
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>{summary}</div>
                    )}
                  </div>

                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    {sig ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent }}>✓ Signed</div>
                        <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                          {formatTime(sig.signed_at)}
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>Not signed</div>
                    )}
                  </div>
                </div>

                {/* Signer + amendments row */}
                {sig && (
                  <div style={{
                    padding: '6px 14px 8px 34px',
                    borderTop: `1px solid ${COLORS.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                      {decodeHtml(sig.signer)} · {sig.role}
                    </span>
                    {amendCount > 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: COLORS.warning,
                        background: COLORS.warningLight, padding: '1px 6px', borderRadius: 4,
                      }}>
                        {amendCount} amendment{amendCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Vitals separate card (not in SECTIONS but has data) */}
          {sectionData.vitals && (
            <div style={{
              background: COLORS.surface, borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              padding: '10px 14px', marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: sectionData.vitals.reading_count > 0 ? COLORS.primary : COLORS.border }} />
              <span style={{ fontSize: 16, flexShrink: 0 }}>🩺</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Vitals</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  {sectionSummary('vitals', sectionData.vitals)}
                </div>
              </div>
              {sectionData.vitals.abnormal_count > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.warning, background: COLORS.warningLight, padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                  ⚠ Abnormal
                </span>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}